import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, POTENTIAL_LABELS } from "@/lib/format";
import { StatCard, PageHeader } from "@/components/Common";
import {
  Wallet,
  Building2,
  Users,
  UserRound,
  Flame,
  CalendarClock,
  TrendingUp,
  Download,
  Boxes,
  Cake,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";

const POT_COLORS = ["#64748B", "#3B82F6", "#059669", "#D97706", "#EF4444"];

async function downloadReport(kind) {
  const res = await api.get(`/reports/${kind}`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `wealthoo_${kind}_report.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function ExportButton({ kind, testid }) {
  return (
    <button
      onClick={() => downloadReport(kind)}
      data-testid={testid}
      className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 row-hover"
    >
      <Download className="h-3.5 w-3.5" /> Export CSV
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [birthdays, setBirthdays] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/birthdays").then((r) => setBirthdays(r.data));
  }, []);

  if (!data)
    return <div className="text-muted-foreground py-20 text-center">Loading dashboard…</div>;

  const productChart = data.product_business
    .filter((p) => p.clients > 0)
    .map((p) => ({ name: p.label, value: p.value, clients: p.clients, key: p.key }));

  const potentialChart = Object.entries(data.potential_distribution).map(([k, v]) => ({
    name: `${k}★ ${POTENTIAL_LABELS[k]}`,
    value: v,
    star: Number(k),
  }));

  return (
    <div className="animate-fade-up space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle={
          user.role === "super_admin"
            ? "Organization-wide performance across all branches"
            : user.role === "admin"
            ? "Your branch performance overview"
            : "Your client portfolio overview"
        }
      >
        <ExportButton kind="clients" testid="export-clients-button" />
      </PageHeader>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-aum" label="Total AUM" value={formatINR(data.total_aum)} icon={Wallet} sub="Assets under management" onClick={() => navigate("/clients")} />
        {user.role === "super_admin" && (
          <StatCard testid="stat-branches" label="Active Branches" value={data.total_branches} icon={Building2} accent="gold" onClick={() => navigate("/branches")} />
        )}
        {user.role !== "manager" && (
          <StatCard testid="stat-managers" label="Managers" value={data.total_managers} icon={Users} accent="info" onClick={() => navigate("/managers")} />
        )}
        <StatCard testid="stat-clients" label="Total Clients" value={data.total_clients} icon={UserRound} sub={`${data.new_clients} new this month`} onClick={() => navigate("/clients")} />
        <StatCard testid="stat-crosssell" label="Cross-Sell Leads" value={data.cross_sell_count} icon={Flame} accent="gold" onClick={() => navigate("/cross-sell")} />
        {user.role === "manager" && (
          <StatCard testid="stat-followups" label="Follow-ups" value={data.pending_followups} icon={CalendarClock} accent="info" onClick={() => navigate("/followups")} />
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Product business */}
        <div className="lg:col-span-2 card-surface rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h3 className="font-display font-semibold">Product-wise Business (AUM)</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={productChart} margin={{ left: -10 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94A3B8" }} angle={-25} textAnchor="end" height={70} interval={0} />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(v) => formatINR(v)} />
              <Tooltip
                contentStyle={{ background: "#0E1524", border: "1px solid #1E293B", borderRadius: 10, fontSize: 12 }}
                formatter={(v, n, p) => [formatINR(v) + ` · ${p.payload.clients} clients`, "AUM"]}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#3B82F6" cursor="pointer" onClick={(d) => d && d.key && navigate(`/clients?product=${d.key}`)} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Potential distribution */}
        <div className="card-surface rounded-xl p-5">
          <h3 className="font-display font-semibold mb-4">Client Potential</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={potentialChart} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                {potentialChart.map((e) => (
                  <Cell key={e.star} fill={POT_COLORS[e.star - 1]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: "#0E1524", border: "1px solid #1E293B", borderRadius: 10, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {potentialChart.map((e) => (
              <div key={e.star} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: POT_COLORS[e.star - 1] }} />
                  {e.name}
                </span>
                <span className="num text-muted-foreground">{e.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Client Birthdays */}
      {birthdays.length > 0 && (
        <div className="card-surface rounded-xl p-5" data-testid="dashboard-birthdays">
          <div className="flex items-center gap-2 mb-4">
            <Cake className="h-4 w-4 text-gold" />
            <h3 className="font-display font-semibold">Client Birthdays</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {birthdays.slice(0, 6).map((b) => (
              <div key={b.client_id} data-testid={`dash-birthday-${b.client_id}`} className={`rounded-lg border p-3 flex items-center gap-3 ${b.days_until === 0 ? "border-gold/50 bg-gold/10" : "border-border"}`}>
                <span className="text-2xl">🎂</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{b.name}</div>
                  <div className="text-xs text-muted-foreground num">{b.days_until === 0 ? "Today" : b.days_until === 1 ? "Tomorrow" : `In ${b.days_until} days`} · {b.date_label}</div>
                </div>
                <button onClick={() => navigate(`/clients/${b.client_id}`)} data-testid={`dash-birthday-view-${b.client_id}`} className="text-xs font-medium text-primary hover:underline shrink-0">View Client</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product-wise assets grid (clickable) */}
      <div className="card-surface rounded-xl p-5" data-testid="product-assets-grid">
        <div className="flex items-center gap-2 mb-4">
          <Boxes className="h-4 w-4 text-primary" />
          <h3 className="font-display font-semibold">Product-wise Assets</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {productChart.length === 0 && <div className="text-sm text-muted-foreground">No product holdings yet.</div>}
          {productChart.map((p) => (
            <button key={p.key} onClick={() => navigate(`/clients?product=${p.key}`)} data-testid={`product-asset-${p.key}`} className="text-left rounded-lg border border-border p-4 row-hover hover:border-primary/40 hover:bg-secondary">
              <div className="text-sm font-medium truncate">{p.name}</div>
              <div className="mt-1 font-display text-lg font-bold num text-primary">{formatINR(p.value)}</div>
              <div className="text-xs text-muted-foreground num">{p.clients} clients</div>
            </button>
          ))}
        </div>
      </div>

      {/* Performance tables */}
      {data.branch_performance && (
        <PerfTable title="Branch Performance" rows={data.branch_performance} cols={["managers", "clients"]} testid="branch-performance" kind="branch" />
      )}
      {data.manager_performance && (
        <PerfTable title="Manager Performance" rows={data.manager_performance} cols={["clients"]} testid="manager-performance" kind="manager" />
      )}
    </div>
  );
}

function PerfTable({ title, rows, cols, testid, kind }) {
  const navigate = useNavigate();
  return (
    <div className="card-surface rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold">{title}</h3>
        {kind && <ExportButton kind={kind} testid={`export-${kind}-button`} />}
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => {
          const max = Math.max(...rows.map((x) => x.aum), 1);
          return (
            <div key={`${r.name}-${i}`} onClick={() => r.id && navigate(kind === "branch" ? `/branches/${r.id}` : `/managers/${r.id}`)} className="flex items-center gap-4 py-2 border-b border-border/50 last:border-0 cursor-pointer row-hover hover:bg-secondary rounded-lg px-2">
              <div className="w-6 text-center text-xs text-muted-foreground num">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{r.name}</div>
                <div className="h-1.5 mt-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${(r.aum / max) * 100}%` }} />
                </div>
              </div>
              {cols.map((c) => (
                <div key={c} className="text-right w-20 hidden sm:block">
                  <div className="text-sm num">{r[c]}</div>
                  <div className="text-[10px] text-muted-foreground uppercase">{c}</div>
                </div>
              ))}
              <div className="text-right w-24">
                <div className="text-sm font-semibold num text-primary">{formatINR(r.aum)}</div>
                <div className="text-[10px] text-muted-foreground uppercase">AUM</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
